import React, { useState } from 'react';
import { Share2, Twitter, Facebook, Linkedin, Copy, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ShareToSocial = ({ stats, onClose }) => {
  const [copied, setCopied] = useState(false);

  // Generate share text
  const shareText = `🔥 I've completed ${stats.completedToday || 0} habits today with a ${stats.currentStreak || 0}-day streak on Habit2Goals! Building better habits, one day at a time. 💪 #Habits #SelfImprovement #Habit2Goals`;
  
  const shareUrl = window.location.origin;
  
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const socialLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleShare = (platform) => {
    window.open(socialLinks[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md gradient-card rounded-2xl shadow-2xl overflow-hidden" data-testid="share-modal">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
              <Share2 className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-100">
                Share Your Progress
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Inspire others with your habit journey!
              </p>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="p-6 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {shareText}
          </p>
        </div>

        {/* Social Buttons */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Button
              onClick={() => handleShare('twitter')}
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
              data-testid="share-twitter"
            >
              <Twitter className="w-5 h-5 text-[#1DA1F2]" />
              <span className="text-xs">Twitter</span>
            </Button>
            
            <Button
              onClick={() => handleShare('facebook')}
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
              data-testid="share-facebook"
            >
              <Facebook className="w-5 h-5 text-[#1877F2]" />
              <span className="text-xs">Facebook</span>
            </Button>
            
            <Button
              onClick={() => handleShare('linkedin')}
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
              data-testid="share-linkedin"
            >
              <Linkedin className="w-5 h-5 text-[#0A66C2]" />
              <span className="text-xs">LinkedIn</span>
            </Button>
          </div>

          {/* Copy Link */}
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            data-testid="copy-share-link"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy to Clipboard</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareToSocial;
